import { Controller } from "@hotwired/stimulus"

// Lucide icons for the composer pills, one per kind of pick: page (file), report (dashboard),
// chart (bar chart), data table (database), record (table grid).
const svg = (body) => `<svg class="laz-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`
const PILL_ICONS = {
  page:   svg(`<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="14 3 14 9 20 9"/>`),
  report: svg(`<rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>`),
  chart:  svg(`<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>`),
  table:  svg(`<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/>`),
  record: svg(`<path d="M12 3v18"/><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/>`)
}
const REMOVE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`

export default class extends Controller {
  static targets = ["input", "dropdown", "chipZone", "composerInner"]
  // recordSearch: the composer's owner may search entity records (Organization#record_search_allowed?).
  // Anyone else would only be redirected away by entities/search, so it isn't asked at all.
  static values  = { orgSlug: String, recordSearch: Boolean }

  connect() {
    this.activeIndex = -1
    this._searchTimer = null
    this._mentionActive = false
    this._handleClickOutside = this._clickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)

    // Chip is sticky-until-changed: it persists across sends. Server re-renders
    // the composer after each turn with the chip pre-populated from
    // chat.other_data['active_page_id'], so client-side clearing is unnecessary
    // (and would fight the server render). To detach, user clicks the pill ✕.
    //
    // Push the textarea's text clear of the pills. The whole zone is watched, not measured once:
    // pills come and go, and a composer inside a hidden panel (the story-page PharOS panel) lays
    // them out at 0px wide until the panel opens.
    if (this.hasChipZoneTarget && this.hasComposerInnerTarget) {
      this._chipObserver = new ResizeObserver(() => {
        const w = this.chipZoneTarget.getBoundingClientRect().width
        this.composerInnerTarget.style.setProperty("--chip-inset", w > 0 ? `${Math.round(w + 8)}px` : "0")
      })
      this._chipObserver.observe(this.chipZoneTarget)
    }

    this._handleFormData = this._addMentions.bind(this)
    this.element.addEventListener("formdata", this._handleFormData)
  }

  disconnect() {
    document.removeEventListener("click", this._handleClickOutside)
    this.element.removeEventListener("formdata", this._handleFormData)
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this._chipObserver?.disconnect()
  }

  // The pills ARE the picks — any number, of any kind. The form learns them as it is sent (formdata
  // fires while the submission is built, so neither the textarea nor a hidden field is kept in step):
  //   * the first page pill is the question's page context (message[mentioned_page_id]);
  //   * the first pinned report / page / chart scopes edit + debug (message[report_*]);
  //   — one of each, because that is what the pipeline takes per turn — and
  //   * every other pick goes in front of the question as its @mention, so the AI reads it by name.
  _addMentions(event) {
    if (!this.hasChipZoneTarget) return
    const fd = event.formData
    const pills = [...this.chipZoneTarget.querySelectorAll(".laz-page-chip")]
    const pages = pills.filter(p => p.dataset.pageId)
    fd.set("message[mentioned_page_id]", pages[0]?.dataset.pageId || "")

    const ref = JSON.parse(pills.find(p => p.dataset.ref)?.dataset.ref || "{}")
    for (const [field, key] of [["report_story_id", "story"], ["report_page_id", "page"],
      ["report_component_id", "component"], ["report_ref_name", "name"], ["report_ref_level", "level"]]) {
      fd.set(`message[${field}]`, ref[key] || "")
    }

    const mentions = [
      ...pages.slice(1).map(p => `@${p.dataset.label || p.querySelector(".laz-chip-name").textContent.trim()}`),
      ...pills.filter(p => p.dataset.mention).map(p => p.dataset.mention)
    ]
    if (mentions.length) fd.set("content", `${mentions.join(" ")} ${fd.get("content") || ""}`.trim())
  }

  onInput() {
    const input = this.inputTarget
    const val = input.value
    const pos = input.selectionStart

    // Find the last @ before cursor that isn't already closed with ]
    const before = val.slice(0, pos)
    const atIdx = before.lastIndexOf("@")

    if (atIdx === -1 || before.slice(atIdx).includes("]")) {
      this._hide()
      return
    }

    const query = before.slice(atIdx + 1).trim()
    this._mentionActive = true

    if (query.length < 2) {
      this._showEmpty()
      return
    }

    this._search(query)
  }

  onKeydown(event) {
    if (!this._mentionActive) return

    const items = this.dropdownTarget.querySelectorAll("[data-mention-item]")

    if (event.key === "Escape") {
      event.preventDefault()
      this._hide()
      return
    }

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1)
      this._highlightItem(items)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
      this._highlightItem(items)
      return
    }

    // → opens the highlighted report; ← comes back. Mirrors Mini, and matches what a finder-style
    // list trains people to expect.
    if (event.key === "ArrowRight" && this.activeIndex >= 0 && items.length > 0) {
      const el = items[this.activeIndex]
      if (el?.dataset.level === "report" && this._drillInto(el.dataset.storyId)) event.preventDefault()
      return
    }

    if (event.key === "ArrowLeft") {
      if (this._drillOut()) event.preventDefault()
      return
    }

    if (event.key === "Enter" && this.activeIndex >= 0 && items.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      const item = items[this.activeIndex]
      this._selectFromEl(item)
      return
    }
  }

  select(event) {
    this._selectFromEl(event.currentTarget)
  }

  // Every pick is one more pill; a pick already in the zone is not added twice.
  _selectFromEl(el) {
    const d = el.dataset
    this._stripQuery()
    if (d.mentionType === "page") {
      this._addPill("page", d.display, `page:${d.pageId}`, { pageId: d.pageId, label: `${d.template} › ${d.display}` })
    } else if (d.mentionType === "report" && d.level === "page") {
      // A page from the report list is page context too — and pinned for edit/debug like any report pick.
      this._addPill("page", d.name, `page:${d.pageId}`, {
        pageId: d.pageId, label: d.display,
        ref: JSON.stringify({ story: d.storyId, page: d.pageId, name: d.name, level: "page" })
      })
    } else if (d.mentionType === "report") {
      // A report / chart: its NAME goes in front of the question (so the answer reads naturally),
      // its ids scope edit/debug. Without the ids the lanes fall back to name inference.
      this._addPill(d.level === "component" ? "chart" : "report", d.name, `ref:${d.storyId}:${d.pageId}:${d.componentId}`, {
        mention: `@${d.name}`,
        ref: JSON.stringify({ story: d.storyId, page: d.pageId, component: d.componentId, name: d.name, level: d.level })
      })
    } else {
      // A data table or record: `@Name [bq_name]`, so the question names the exact table.
      const mention = `@${d.display} [${d.bqName}]`
      this._addPill(d.kind === "table" ? "table" : "record", d.display, mention, { mention })
    }
    this._hide()
  }

  // Private

  _search(query) {
    if (this._searchTimer) clearTimeout(this._searchTimer)

    this._searchTimer = setTimeout(async () => {
      // Fire all searches in parallel — reports/pages (small indexes) usually beat entities
      const q = encodeURIComponent(query)
      const [reports, pages, tables, entities] = await Promise.all([
        this._fetchList(`/${this.orgSlugValue}/chats/report_search?q=${q}`),
        this._fetchList(`/${this.orgSlugValue}/chats/page_search?q=${q}`),
        this._fetchList(`/${this.orgSlugValue}/chats/table_search?q=${q}`),
        this.recordSearchValue ? this._fetchList(`/${this.orgSlugValue}/entities/search?q=${q}`) : []
      ])
      if (!this._mentionActive) return // the @ was deleted or picked while the searches ran
      this._last = { pages, entities, reports, tables }
      this._drillStoryId = null
      this._renderResults(pages, entities, reports, tables)
    }, 300)
  }

  // One source failing — an error status, a redirect that lands on an HTML page, a dropped
  // connection — contributes no rows; it must never blank the other sources' results.
  async _fetchList(url) {
    try {
      const resp = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!resp.ok) return []
      const data = await resp.json()
      return Array.isArray(data) ? data : []
    } catch (_) {
      return []
    }
  }

  // ONE ROW SHAPE FOR EVERY KIND. Reports, pages and records used to render as three
  // different layouts under three group headings; the headings cost a row each to say
  // what a pill on the row says better, and they broke arrow-key navigation into
  // stretches of unselectable rows.
  //
  // Order is deliberate: kind, then name, then the thing that tells two same-named
  // rows apart. Reports here are routinely called "P&L" twice.
  _row(attrs, kind, label, sub, opts = {}) {
    const pairs = Object.entries(attrs)
      .map(([k, v]) => `data-${k}="${this._escapeAttr(String(v ?? ""))}"`).join(" ")
    return `<div data-mention-item ${pairs} data-action="click->chat-mention#select" class="laz-men-row">
      <span class="laz-men-kind">${this._escapeHtml(kind)}</span>
      <span class="laz-men-label">${this._escapeHtml(label)}</span>
      ${sub ? `<span class="laz-men-sub">${this._escapeHtml(sub)}</span>` : ""}
      ${opts.more ? `<span class="laz-men-arrow" data-action="click->chat-mention#drillIn">\u203a</span>` : ""}
    </div>`
  }

  // DRILLING IS A FILTER, NOT A FETCH. report_search already returns a matched report's pages and
  // components alongside it — going "into" a report is therefore narrowing what is already on
  // screen, which is why it can be instant and cannot fail.
  _drillInto(storyId) {
    const inside = (this._last?.reports || []).filter(r => String(r.story_id) === String(storyId) && r.level !== "report")
    if (inside.length === 0) return false      // nothing under it — the arrow does nothing rather than emptying the list
    this._drillStoryId = storyId
    this._renderResults([], [], inside)
    return true
  }

  _drillOut() {
    if (!this._drillStoryId) return false
    this._drillStoryId = null
    const l = this._last || { pages: [], entities: [], reports: [], tables: [] }
    this._renderResults(l.pages, l.entities, l.reports, l.tables)
    return true
  }

  _renderResults(pages, entities, reports = [], tables = []) {
    this.activeIndex = -1
    const dd = this.dropdownTarget

    if (pages.length === 0 && entities.length === 0 && reports.length === 0 && tables.length === 0) {
      dd.innerHTML = `<div class="laz-men-empty">No matching reports, pages, tables or records</div>`
      dd.classList.remove("hidden")
      return
    }

    let html = ""

    if (this._drillStoryId) {
      const parent = (this._last?.reports || []).find(r => String(r.story_id) === String(this._drillStoryId) && r.level === "report")
      html += `<div class="laz-men-crumbs">
        <span class="laz-men-crumb" data-action="click->chat-mention#drillOut">‹ all results</span>
        <span class="laz-men-crumb-cur">${this._escapeHtml(parent?.name || "")}</span>
      </div>`
    }

    // A report / page / component to edit or debug. The picked NAME goes in front of the question;
    // the ids ride on the pill (see _selectFromEl / _addMentions) so the pipeline scopes the lane
    // to exactly what was pinned.
    reports.forEach(r => {
      const disp = r.label || r.name || ""
      const kind = r.level === "component" ? "CHART" : (r.level === "page" ? "PAGE" : "STORY")
      // The NAME on the left, its CONTEXT on the right. `label` arrives as a breadcrumb
      // ("Report › Page › Chart"); showing all of it left-aligned pushes every name out of
      // line and truncates the part being searched for. So the leaf is the label and the
      // trail above it is the sub — or the slug, for a report, which has no trail.
      const parts = disp.split(" › ")
      const leaf  = r.name || parts[parts.length - 1] || disp
      const trail = parts.length > 1 ? parts.slice(0, -1).join(" › ") : (r.slug || "")
      html += this._row({
        "mention-type": "report", "display": disp, "name": r.name || disp,
        "level": r.level || "", "story-id": r.story_id || "",
        "page-id": r.page_id || "", "component-id": r.component_id || ""
      }, kind, leaf, trail, { more: r.level === "report" })
    })

    // Pages — the anchor context for page-scoped analysis. The report it belongs to is
    // the sub-label, because a page called "Summary" exists in a dozen reports. A page the
    // report list already shows isn't listed twice (both picks make the same pill).
    const listed = new Set(reports.filter(r => r.level === "page").map(r => String(r.page_id)))
    pages.filter(page => !listed.has(String(page.id))).forEach(page => {
      html += this._row(
        { "mention-type": "page", "display": page.page_name, "page-id": page.id, "template": page.template_name || "" },
        "PAGE", page.page_name, page.template_name || ""
      )
    })

    // Data tables — picked like a record (`@Name [bq_name]`), so the question names the exact table.
    tables.forEach(t => {
      html += this._row(
        { "mention-type": "entity", "display": t.display, "bq-name": t.bq_name, "kind": "table" },
        "TABLE", t.display, "Data table"
      )
    })

    // Records, grouped in the data but flattened here — the table name rides on the row
    // as its sub-label instead of costing a heading.
    entities.forEach(item => {
      html += this._row(
        { "mention-type": "entity", "display": item.display, "bq-name": item.bq_name, "kind": "record" },
        "RECORD", item.display, item.table_name || ""
      )
    })

    dd.innerHTML = html
    dd.classList.remove("hidden")
  }

  // The chevron is a real control now, not decoration. stopPropagation so opening a report is
  // never mistaken for picking it.
  drillIn(event) {
    event.preventDefault(); event.stopPropagation()
    const row = event.currentTarget.closest("[data-mention-item]")
    if (row) this._drillInto(row.dataset.storyId)
  }

  drillOut(event) {
    event.preventDefault(); event.stopPropagation()
    this._drillOut()
  }

  _showEmpty() {
    const dd = this.dropdownTarget
    dd.innerHTML = `<div class="laz-men-empty">Type to search records…</div>`
    dd.classList.remove("hidden")
  }

  _hide() {
    this._mentionActive = false
    this.activeIndex = -1
    this._drillStoryId = null
    if (this._searchTimer) clearTimeout(this._searchTimer)
    this.dropdownTarget.classList.add("hidden")
  }

  // Take the typed `@query` out of the text — the pick shows as a pill instead.
  _stripQuery() {
    const input = this.inputTarget
    const pos = input.selectionStart
    const atIdx = input.value.slice(0, pos).lastIndexOf("@")
    if (atIdx === -1) return
    input.value = input.value.slice(0, atIdx) + input.value.slice(pos)
    input.focus()
    input.setSelectionRange(atIdx, atIdx)
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  _addPill(icon, label, key, data) {
    if (!this.hasChipZoneTarget) return
    if ([...this.chipZoneTarget.children].some(c => c.dataset.key === key)) return
    this.chipZoneTarget.appendChild(this._pill(icon, label, { ...data, key }))
  }

  // Same markup as the server-rendered page pill (chats/_page_chip.html.erb).
  _pill(icon, label, data) {
    const chip = document.createElement("span")
    chip.className = "laz-page-chip"
    Object.assign(chip.dataset, data)
    chip.innerHTML = `${PILL_ICONS[icon]}<span class="laz-chip-name">${this._escapeHtml(label)}</span>` +
      `<button type="button" class="laz-chip-remove" title="Remove" data-action="click->chat-mention#removeChip">${REMOVE_ICON}</button>`
    return chip
  }

  // Just the pill — what it meant is read off the pills that are left when the form is sent.
  removeChip(event) {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.closest(".laz-page-chip")?.remove()
    if (this.hasInputTarget) this.inputTarget.focus()
  }

  _highlightItem(items) {
    items.forEach((item, i) => {
      const on = i === this.activeIndex
      item.classList.toggle("is-active", on)
      // The list scrolls at 280px. Without this, arrowing past the fold moves the selection
      // somewhere the user cannot see, and Enter picks a row they never looked at.
      if (on) item.scrollIntoView({ block: "nearest" })
    })
  }

  _clickOutside(event) {
    if (!this._mentionActive) return
    if (this.element.contains(event.target)) return
    this._hide()
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return str.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
